/**
 * UGC video carousel: an endless loop, so there is never empty space beside the cards.
 * The cards are repeated before and after the real set; after each move the view is quietly
 * brought back to the real set. Videos play inside their card, one at a time.
 */
class AvalonUgcCarousel extends HTMLElement {
  #track = null;
  #originals = [];
  #cards = [];
  #dots = [];
  #count = 0;
  #sides = 0; // repeated sets before and after the real set
  #abs = 0; // index of the centered card within #cards
  #frame = 0;
  #settleTimer = 0;
  #touching = false;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;

    this.#originals = Array.from(this.querySelectorAll('[data-card]'));
    this.#count = this.#originals.length;
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.#cards = this.#originals.slice();

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track.addEventListener('keydown', this.#onKeydown);
    this.#track.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.#track.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1));
    this.#dots.forEach((dot, index) => dot.addEventListener('click', () => this.#goToReal(index)));
    this.addEventListener('click', this.#onPlayClick);
    this.addEventListener('play', this.#onVideoPlay, true);
    window.addEventListener('resize', this.#onResize, { passive: true });
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    if (this.#count > 1) this.#loop();

    const start = Math.min(Number(this.dataset.start) || 0, this.#count - 1);
    this.#abs = this.#sides * this.#count + start;
    this.#center(this.#abs, true);
    this.#syncDots();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('keydown', this.#onKeydown);
    this.#track?.removeEventListener('touchstart', this.#onTouchStart);
    this.#track?.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('click', this.#onPlayClick);
    this.removeEventListener('play', this.#onVideoPlay, true);
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
  }

  /** Repeat the cards before and after the real set, enough to always fill the screen. */
  #loop() {
    const first = this.#originals[0];
    const pitch = first.offsetWidth + (parseFloat(getComputedStyle(this.#track).columnGap) || 0);
    const visible = Math.ceil(this.#track.clientWidth / Math.max(pitch, 1)) + 2;
    this.#sides = Math.ceil(visible / this.#count) + 1;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    for (let set = 0; set < this.#sides; set++) {
      this.#originals.forEach((card) => {
        before.append(this.#clone(card));
        after.append(this.#clone(card));
      });
    }

    this.#track.prepend(before);
    this.#track.append(after);
    this.#cards = Array.from(this.#track.querySelectorAll('[data-card], [data-clone]'));
  }

  #clone(card) {
    const copy = card.cloneNode(true);
    copy.removeAttribute('data-card');
    copy.removeAttribute('data-shopify-editor-block');
    copy.setAttribute('data-clone', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('button, video, a').forEach((el) => el.setAttribute('tabindex', '-1'));
    return copy;
  }

  get #real() {
    return ((this.#abs % this.#count) + this.#count) % this.#count;
  }

  #syncDots() {
    this.#dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.#real)));
  }

  #position(abs) {
    const card = this.#cards[abs];
    return card.offsetLeft - (this.#track.clientWidth - card.offsetWidth) / 2;
  }

  #center(abs, instant = false) {
    const behavior = instant || this.#motionQuery.matches ? 'instant' : 'smooth';
    this.#track.scrollTo({ left: this.#position(abs), behavior });
  }

  #step(direction) {
    if (this.#count < 2) return;

    this.#abs = Math.max(0, Math.min(this.#cards.length - 1, this.#abs + direction));
    this.#syncDots();
    this.#center(this.#abs);
  }

  #goToReal(index) {
    // Take the shortest way round the loop
    let delta = index - this.#real;
    if (delta > this.#count / 2) delta -= this.#count;
    if (delta < -this.#count / 2) delta += this.#count;

    this.#step(delta);
  }

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #onTouchStart = () => {
    this.#touching = true;
  };

  #onTouchEnd = () => {
    this.#touching = false;
    this.#onScroll();
  };

  #onScroll = () => {
    if (!this.#frame) {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#trackActive();
      });
    }

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#settle(), 140);
  };

  /** Follow the card nearest the center while the visitor swipes. */
  #trackActive() {
    const center = this.#track.scrollLeft + this.#track.clientWidth / 2;
    let nearest = this.#abs;
    let best = Infinity;

    this.#cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });

    if (nearest !== this.#abs) {
      this.#abs = nearest;
      this.#syncDots();
      this.#pauseOthers(this.#cards[nearest]);
    }
  }

  /** After the view stops, quietly move it back to the real set of cards. */
  #settle() {
    if (this.#touching || this.#count < 2) return;

    const realStart = this.#sides * this.#count;
    if (this.#abs >= realStart && this.#abs < realStart + this.#count) return;

    this.#abs = realStart + this.#real;

    const snap = this.#track.style.scrollSnapType;
    this.#track.style.scrollSnapType = 'none';
    this.#track.scrollTo({ left: this.#position(this.#abs), behavior: 'instant' });
    requestAnimationFrame(() => {
      this.#track.style.scrollSnapType = snap;
    });
  }

  #onResize = () => {
    this.#center(this.#abs, true);
  };

  #pauseOthers(activeCard) {
    this.querySelectorAll('[data-video]').forEach((video) => {
      if (!activeCard?.contains(video)) video.pause();
    });
  }

  #onPlayClick = (event) => {
    const button = event.target.closest('[data-play]');
    if (!button) return;

    const media = button.closest('.avalon-ugc__media');
    const video = media?.querySelector('[data-video]');
    if (!video) return;

    media.classList.add('is-playing');
    video.hidden = false;
    video.muted = false;
    video.controls = true;
    video.play().catch(() => {
      // Autoplay can be refused; the native controls let the visitor start it
    });
  };

  #onVideoPlay = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;

    this.querySelectorAll('[data-video]').forEach((video) => {
      if (video !== event.target) video.pause();
    });
  };

  #onBlockSelect = (event) => {
    const card = event.target.closest?.('[data-card]');
    const index = this.#originals.indexOf(card);
    if (index < 0) return;

    this.#abs = this.#sides * this.#count + index;
    this.#syncDots();
    this.#center(this.#abs, true);
  };
}

if (!customElements.get('avalon-ugc-carousel')) {
  customElements.define('avalon-ugc-carousel', AvalonUgcCarousel);
}
